import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthApiService } from '@services/auth-api.service';
import { LogoutService } from '@services/logout.service';
import { ToastrService } from '@services/toaster.service';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError, retry, switchMap, take } from 'rxjs/operators';

export const SKIP_ERROR_SNACKBAR = new HttpContextToken<boolean>(() => false);

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private logoutService: LogoutService,
    private authApiService: AuthApiService
  ) {}

  loginRedirectMessages = [
    'Invalid token',
    'Invalid token due to logout',
    'Error validating token - please reset your password and try again',
    'You are not a member of this team - please ask your team lead to give you access.',
    'Token validation failed: Signature has expired',
    'EXPIRED TOKEN - Signature has expired',
    'Unable to log in for an unknown reason. Please contact support.'
  ];

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      retry(0),
      catchError((error: HttpErrorResponse) => {
        if (error.url.includes('/public/heartbeat')) return EMPTY;
        const skipError = request.context.get(SKIP_ERROR_SNACKBAR);

        const errorMessage = this.normalizeErrorMessage(error?.error?.message);

        if (
          !skipError &&
          errorMessage &&
          !this.isLoginRedirectMessage(errorMessage) &&
          !request.url.includes('/info/check_availability')
        ) {
          this.toastrService.showMessage(errorMessage, 2000);
        }

        if (
          error.status === 401 &&
          this.isLoginRedirectMessage(errorMessage) &&
          !this.router.url.includes('login') &&
          !this.logoutService.loggingOut()
        ) {
          const currentUrl = this.router.url;
          return this.authApiService.getNewAccessToken().pipe(
            take(1),
            switchMap((newToken: { token: string; refresh_token: string }) => {
              if (newToken && newToken.token) {
                localStorage.setItem('auth_token', newToken.token);
                localStorage.setItem('refresh_token', newToken.refresh_token);

                const clonedRequest = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken.token}`
                  }
                });

                return next.handle(clonedRequest);
              }
              this.redirectToLogin(currentUrl);
              return throwError(() => error);
            }),
            catchError((refreshTokenError) => {
              this.redirectToLogin(currentUrl);
              return throwError(() => refreshTokenError);
            })
          );
        }

        return throwError(() => error);
      })
    );
  }

  private normalizeErrorMessage(message: unknown): string | undefined {
    if (!message) return undefined;
    if (typeof message === 'string') return message;
    if (Array.isArray(message)) return message.join(', ');
    return String(message);
  }

  private isLoginRedirectMessage(message: string | undefined): boolean {
    if (!message) return false;
    const lowerMessage = message.toLowerCase();
    return this.loginRedirectMessages.some((m) => m.toLowerCase() === lowerMessage);
  }

  private redirectToLogin(returnUrl: string) {
    this.toastrService.showMessage('Your session has expired, please log in again.', 5000);
    this.logoutService.logout({ next: returnUrl.replace('/', '') }).subscribe();
  }
}
